import React, { useEffect, useRef, useState } from 'react';
import { motion, useAnimationFrame, useReducedMotion } from 'framer-motion';
interface Pollen {
  id: number;
  x: number;
  y: number;
  r: number;
}
const CYCLE_MS = 16000;
const DROP_EVERY = 0.025;
export function BumblebeePath() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const beeRef = useRef<SVGGElement>(null);
  const [size, setSize] = useState({
    w: 1000,
    h: 120
  });
  const [pollen, setPollen] = useState<Pollen[]>([]);
  const lastDrop = useRef(0);
  const lastProgress = useRef(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setSize({
        w: entry.contentRect.width,
        h: entry.contentRect.height
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const {
    w,
    h
  } = size;
  const d = `M ${w * 0.1} ${h * 0.7} C ${w * 0.2} ${h * 0.05}, ${w * 0.34} ${h * 0.05}, ${w * 0.42} ${h * 0.55} S ${w * 0.6} ${h * 0.95}, ${w * 0.66} ${h * 0.4} S ${w * 0.82} ${h * 0.05}, ${w * 0.9} ${h * 0.6}`;
  useAnimationFrame(t => {
    const path = pathRef.current;
    const bee = beeRef.current;
    if (!path || !bee) return;
    const total = path.getTotalLength();
    const progress = reduce ? 0.5 : t % CYCLE_MS / CYCLE_MS;
    const eased = progress < 0.92 ? progress / 0.92 : 1;
    const len = eased * total;
    const p = path.getPointAtLength(len);
    const p2 = path.getPointAtLength(Math.min(total, len + 1));
    const angle = Math.atan2(p2.y - p.y, p2.x - p.x) * 180 / Math.PI;
    bee.setAttribute('transform', `translate(${p.x} ${p.y}) rotate(${angle})`);
    bee.style.opacity = progress > 0.95 ? String(Math.max(0, (1 - progress) * 20)) : '1';
    if (reduce) return;
    if (progress < lastProgress.current) {
      setPollen([]);
      lastDrop.current = 0;
    }
    lastProgress.current = progress;
    if (eased - lastDrop.current >= DROP_EVERY && eased < 1) {
      lastDrop.current = eased;
      const id = Math.round(t);
      setPollen(prev => [...prev, {
        id,
        x: p.x + (Math.random() - 0.5) * 14,
        y: p.y + 6 + Math.random() * 10,
        r: 1.2 + Math.random() * 1.6
      }]);
    }
  });
  return <div ref={wrapRef} className="pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-233" data-mp="1mvcd8yz75ndg">
      <svg width={w} height={h} className="overflow-visible" data-id="element-234" data-mp="2al6nd8s4sxu0">
        <path ref={pathRef} d={d} fill="none" stroke="#0F2442" strokeOpacity="0.08" strokeWidth="1" strokeDasharray="2 6" data-id="element-235" data-mp="2alznvs0hyr3p" />
        {pollen.map(pt => <motion.circle key={pt.id} cx={pt.x} cy={pt.y} r={pt.r} fill="#E3A82B" initial={{
        opacity: 0,
        cy: pt.y - 6
      }} animate={{
        opacity: 0.75,
        cy: pt.y
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-236" data-mp="21l1ip6ebodjj" />)}
        <g ref={beeRef} data-id="element-237" data-mp="u7pzwz0634de">
          <motion.ellipse cx="-1" cy="-5" rx="5" ry="3" fill="#FFFFFF" fillOpacity="0.85" stroke="#0F2442" strokeOpacity="0.25" strokeWidth="0.6" animate={{
          scaleY: [1, 0.35, 1]
        }} transition={{
          duration: 0.12,
          repeat: Infinity
        }} style={{
          originX: 0.5,
          originY: 1
        }} data-id="element-238" data-mp="u89fi92chgo9" />
          <motion.ellipse cx="3" cy="-4.5" rx="4" ry="2.5" fill="#FFFFFF" fillOpacity="0.7" stroke="#0F2442" strokeOpacity="0.2" strokeWidth="0.6" animate={{
          scaleY: [0.35, 1, 0.35]
        }} transition={{
          duration: 0.12,
          repeat: Infinity
        }} style={{
          originX: 0.5,
          originY: 1
        }} data-id="element-239" data-mp="3wxp33dabzof" />
          <ellipse cx="0" cy="0" rx="7" ry="4.2" fill="#0F2442" data-id="element-240" data-mp="3hb11jv160fs5" />
          <rect x="-2.5" y="-4" width="1.8" height="8" fill="#E3A82B" data-id="element-241" data-mp="3ccr4fevvr53w" />
          <rect x="1.2" y="-4" width="1.8" height="8" fill="#E3A82B" data-id="element-242" data-mp="3cchd9nz3mrwm" />
          <circle cx="7" cy="0" r="2.4" fill="#0F2442" data-id="element-243" data-mp="2fmdtwcf9575f" />
        </g>
      </svg>
    </div>;
}